import React, { useState, useEffect } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { Recipe } from '../types/Recipe';

interface WorldRecipe extends Recipe {
  country: string;
  region?: string;
  traditional: boolean;
}

const WorldRecipesPage: React.FC = () => {
  const [recipes, setRecipes] = useState<WorldRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredRecipes, setFilteredRecipes] = useState<WorldRecipe[]>([]);

  // Simuler le chargement des recettes du monde
  useEffect(() => {
    // Dans une application réelle, cela viendrait d'une API ou d'un fichier JSON
    const mockWorldRecipes: WorldRecipe[] = [
      {
        id: 'w1',
        title: 'Paella Valenciana',
        description: 'La paella traditionnelle de Valence avec du poulet, du lapin et des légumes.',
        category: 'plat',
        ingredients: ['riz rond', 'poulet', 'lapin', 'haricots verts', 'tomates', 'safran', 'huile d\'olive'],
        steps: [
          'Faire revenir la viande dans l\'huile d\'olive',
          'Ajouter les légumes et faire revenir',
          'Ajouter le riz et le safran',
          'Verser le bouillon et laisser cuire à feu doux'
        ],
        prepTime: 30,
        cookTime: 25,
        servings: 4,
        difficulty: 'moyen',
        imageUrl: 'https://images.unsplash.com/photo-1534080564583-6be75777b70a',
        createdAt: new Date(),
        updatedAt: new Date(),
        country: 'Espagne',
        region: 'Valence',
        traditional: true
      },
      {
        id: 'w2',
        title: 'Sushi Maki',
        description: 'Rouleaux de riz et de poisson cru enveloppés dans une feuille d\'algue nori.',
        category: 'plat',
        ingredients: ['riz à sushi', 'vinaigre de riz', 'algue nori', 'saumon frais', 'concombre', 'avocat'],
        steps: [
          'Préparer le riz à sushi avec le vinaigre',
          'Étaler le riz sur la feuille de nori',
          'Disposer les ingrédients au centre',
          'Rouler fermement et couper en tranches'
        ],
        prepTime: 45,
        cookTime: 15,
        servings: 2,
        difficulty: 'difficile',
        imageUrl: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c',
        createdAt: new Date(),
        updatedAt: new Date(),
        country: 'Japon',
        region: 'Tokyo',
        traditional: true
      },
      {
        id: 'w3',
        title: 'Coq au Vin',
        description: 'Plat traditionnel français à base de poulet mijoté dans du vin rouge avec des légumes.',
        category: 'plat',
        ingredients: ['poulet', 'vin rouge', 'lardons', 'champignons', 'oignons', 'carottes', 'bouquet garni'],
        steps: [
          'Faire revenir les lardons et réserver',
          'Faire dorer le poulet dans la même cocotte',
          'Ajouter les légumes et faire revenir',
          'Verser le vin rouge et laisser mijoter à feu doux'
        ],
        prepTime: 30,
        cookTime: 90,
        servings: 4,
        difficulty: 'moyen',
        imageUrl: 'https://images.unsplash.com/photo-1600891963935-9e6aa2b5a4f1',
        createdAt: new Date(),
        updatedAt: new Date(),
        country: 'France',
        region: 'Bourgogne',
        traditional: true
      },
      {
        id: 'w4',
        title: 'Pad Thai',
        description: 'Plat thaïlandais à base de nouilles de riz sautées avec des crevettes, du tofu et des légumes.',
        category: 'plat',
        ingredients: ['nouilles de riz', 'crevettes', 'tofu', 'œuf', 'germes de soja', 'cacahuètes', 'sauce de poisson'],
        steps: [
          'Faire tremper les nouilles dans l\'eau chaude',
          'Faire sauter les crevettes et le tofu',
          'Ajouter l\'œuf et les nouilles',
          'Incorporer la sauce et les garnitures'
        ],
        prepTime: 20,
        cookTime: 10,
        servings: 2,
        difficulty: 'facile',
        imageUrl: 'https://images.unsplash.com/photo-1559314809-0d155014e29e',
        createdAt: new Date(),
        updatedAt: new Date(),
        country: 'Thaïlande',
        region: 'Bangkok',
        traditional: true
      },
      {
        id: 'w5',
        title: 'Tiramisu',
        description: 'Dessert italien à base de mascarpone, de café et de biscuits.',
        category: 'dessert',
        ingredients: ['mascarpone', 'œufs', 'sucre', 'café fort', 'biscuits à la cuillère', 'cacao en poudre'],
        steps: [
          'Séparer les blancs des jaunes d\'œufs',
          'Mélanger les jaunes avec le sucre et le mascarpone',
          'Monter les blancs en neige et les incorporer',
          'Tremper les biscuits dans le café et les disposer dans le plat',
          'Alterner couches de crème et biscuits, saupoudrer de cacao'
        ],
        prepTime: 30,
        cookTime: 0,
        servings: 6,
        difficulty: 'facile',
        imageUrl: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9',
        createdAt: new Date(),
        updatedAt: new Date(),
        country: 'Italie',
        region: 'Vénétie',
        traditional: true
      },
      {
        id: 'w6',
        title: 'Tacos al Pastor',
        description: 'Tacos mexicains garnis de porc mariné et cuit à la broche, servis avec ananas et coriandre.',
        category: 'plat',
        ingredients: ['porc', 'tortillas de maïs', 'ananas', 'oignon', 'coriandre', 'citron vert', 'épices'],
        steps: [
          'Mariner la viande avec les épices',
          'Cuire la viande à la broche ou à la poêle',
          'Chauffer les tortillas',
          'Garnir avec la viande, l\'ananas et les condiments'
        ],
        prepTime: 30,
        cookTime: 45,
        servings: 4,
        difficulty: 'moyen',
        imageUrl: 'https://images.unsplash.com/photo-1551504734-5ee1c4a1479b',
        createdAt: new Date(),
        updatedAt: new Date(),
        country: 'Mexique',
        region: 'Mexico',
        traditional: true
      }
    ];

    setTimeout(() => {
      setRecipes(mockWorldRecipes);
      setFilteredRecipes(mockWorldRecipes);
      setLoading(false);
    }, 1000);
  }, []);

  // Filtrer les recettes en fonction des critères sélectionnés
  useEffect(() => {
    let filtered = [...recipes];

    if (selectedCountry) {
      filtered = filtered.filter(recipe => recipe.country === selectedCountry);
    }

    if (selectedRegion) {
      filtered = filtered.filter(recipe => recipe.region === selectedRegion);
    }

    if (selectedType) {
      filtered = filtered.filter(recipe => recipe.category === selectedType);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(recipe => 
        recipe.title.toLowerCase().includes(term) || 
        recipe.description.toLowerCase().includes(term) ||
        recipe.ingredients.some(ing => ing.toLowerCase().includes(term))
      );
    }

    setFilteredRecipes(filtered);
  }, [recipes, selectedCountry, selectedRegion, selectedType, searchTerm]);

  // Obtenir la liste des pays uniques
  const countries = [...new Set(recipes.map(recipe => recipe.country))];
  
  // Obtenir la liste des régions uniques pour le pays sélectionné
  const regions = selectedCountry 
    ? [...new Set(recipes.filter(recipe => recipe.country === selectedCountry).map(recipe => recipe.region).filter(Boolean))]
    : [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow py-20 bg-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold mb-4">Recettes du Monde</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explorez les saveurs des quatre coins du globe et découvrez des plats traditionnels de différentes cultures.
            </p>
          </div>
          
          {/* Carte interactive (simplifiée) */}
          <div className="mb-10 bg-white rounded-xl shadow-md p-6 text-center">
            <h2 className="text-xl font-semibold mb-4">Explorez par pays</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {countries.map(country => (
                <button
                  key={country}
                  className={`px-4 py-2 rounded-full transition-all ${
                    selectedCountry === country 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                  onClick={() => {
                    setSelectedCountry(selectedCountry === country ? '' : country);
                    setSelectedRegion('');
                  }}
                >
                  {country}
                </button>
              ))}
            </div>
            
            {/* Régions si un pays est sélectionné */}
            {selectedCountry && regions.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-medium mb-2">Régions de {selectedCountry}</h3>
                <div className="flex flex-wrap justify-center gap-2">
                  {regions.map(region => (
                    <button
                      key={region}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        selectedRegion === region 
                          ? 'bg-secondary text-white' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                      }`}
                      onClick={() => setSelectedRegion(selectedRegion === region ? '' : region as string)}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Filtres avancés */}
          <div className="mb-8 bg-white rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap gap-4 justify-between items-center">
              <div className="flex-grow">
                <input
                  type="text"
                  placeholder="Rechercher une recette..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div className="flex flex-wrap gap-3">
                {/* Filtre par type */}
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Tous les types</option>
                  <option value="entrée">Entrées</option>
                  <option value="plat">Plats</option>
                  <option value="dessert">Desserts</option>
                  <option value="boisson">Boissons</option>
                </select>
                
                {/* Bouton réinitialiser */}
                <button
                  onClick={() => {
                    setSelectedCountry('');
                    setSelectedRegion('');
                    setSelectedType('');
                    setSearchTerm('');
                  }}
                  className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
          
          {/* Grille de recettes */}
          {loading ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-600">Chargement des recettes du monde...</p>
            </div>
          ) : filteredRecipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRecipes.map((recipe) => (
                <div key={recipe.id} className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  {/* Image et badge pays */}
                  <div className="relative h-48">
                    <img 
                      src={recipe.imageUrl || 'https://via.placeholder.com/300x200?text=No+Image'} 
                      alt={recipe.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded-full text-xs font-medium shadow-sm">
                      {recipe.country}
                    </div>
                    {recipe.traditional && (
                      <div className="absolute bottom-2 left-2 bg-primary text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm">
                        Traditionnel
                      </div>
                    )}
                  </div>
                  
                  {/* Contenu */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2">{recipe.title}</h3>
                    
                    <div className="flex justify-between items-center mb-3">
                      <span className="inline-block bg-gray-100 rounded-full px-3 py-1 text-xs font-medium text-gray-700">
                        {recipe.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        {recipe.prepTime + recipe.cookTime} min
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{recipe.description}</p>
                    
                    <button className="w-full bg-gradient-to-r from-primary to-secondary text-white py-2 rounded-lg hover:opacity-90 transition-opacity">
                      Voir la recette
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold mb-2">Aucune recette trouvée</h3>
              <p className="text-gray-600">
                Aucune recette ne correspond à vos critères de recherche.
              </p>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WorldRecipesPage;
